import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { UserPlus, Shield, Pencil, Copy, Check, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const roleBadge = (role) => {
  const cfg = {
    admin: { bg: 'rgba(220,38,38,0.1)', color: '#DC2626', label: 'Admin' },
    editor: { bg: 'rgba(37,99,235,0.1)', color: '#2563EB', label: 'Editor' },
    user: { bg: 'rgba(148,163,184,0.15)', color: 'var(--text-muted)', label: 'Utente' },
  }[role] || { bg: 'rgba(148,163,184,0.15)', color: 'var(--text-muted)', label: role };
  return <span className="px-2 py-0.5 rounded-sm text-xs" style={{ backgroundColor: cfg.bg, color: cfg.color, fontFamily: 'var(--font-mono)' }}>{cfg.label}</span>;
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'editor', password: '' });
  const [busy, setBusy] = useState(false);
  const [createdCred, setCreatedCred] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch { toast.error('Errore caricamento utenti'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.email) { toast.error('Email obbligatoria'); return; }
    setBusy(true);
    try {
      const res = await api.post('/admin/users/create', {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
        password: form.password || undefined,
      });
      setCreatedCred(res.data);
      setForm({ email: '', name: '', role: 'editor', password: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione utente');
    } finally { setBusy(false); }
  };

  const handleRoleChange = async (user_id, role) => {
    try {
      await api.put(`/admin/users/${user_id}/role`, { role });
      toast.success('Ruolo aggiornato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const copyCreds = () => {
    if (!createdCred) return;
    const text = `Email: ${createdCred.email}\nPassword: ${createdCred.password}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>Utenti</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Gestisci admin ed editor del sito</p>
        </div>
        <Button onClick={() => { setCreatedCred(null); setCreateOpen(true); }} data-testid="user-create-btn"
          className="rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
          <UserPlus className="w-4 h-4 mr-2" /> Nuovo utente
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'var(--font-heading)' }}>Tutti gli utenti ({users.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Nome</th>
                  <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Email</th>
                  <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Ruolo</th>
                  <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Auth</th>
                  <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} style={{ borderBottom: '1px solid var(--border-light)' }} data-testid={`user-row-${u.user_id}`}>
                    <td className="py-3 pr-3 font-medium" style={{ color: 'var(--text-primary)' }}>{u.name}</td>
                    <td className="py-3 pr-3" style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td className="py-3 pr-3">{roleBadge(u.role)}</td>
                    <td className="py-3 pr-3 text-xs" style={{ color: 'var(--text-muted)' }}>{u.auth_provider}</td>
                    <td className="py-3 pr-3">
                      <select value={u.role} onChange={e => handleRoleChange(u.user_id, e.target.value)}
                        className="text-xs px-2 py-1 rounded-sm border" data-testid={`user-role-select-${u.user_id}`}
                        style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-white)' }}>
                        <option value="user">Utente</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info box on permissions */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-xs font-medium mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} /> Permessi per ruolo</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rounded-sm" style={{ backgroundColor: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)' }}>
              <p className="font-semibold mb-1">Admin</p>
              <p style={{ color: 'var(--text-muted)' }}>Accesso completo: Dashboard, Campagne, Codici Sconto, Push, Recensioni, Ordini, Abbonamenti, Analytics, Utenti.</p>
            </div>
            <div className="p-3 rounded-sm" style={{ backgroundColor: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)' }}>
              <p className="font-semibold mb-1">Editor</p>
              <p style={{ color: 'var(--text-muted)' }}>Gestisce contenuti: Dashboard, Campagne, Codici Sconto, Push, Recensioni, Analytics. <strong>Non accede a Ordini/Abbonamenti/Utenti.</strong></p>
            </div>
            <div className="p-3 rounded-sm" style={{ backgroundColor: 'rgba(148,163,184,0.08)', border: '1px solid var(--border-light)' }}>
              <p className="font-semibold mb-1">Utente</p>
              <p style={{ color: 'var(--text-muted)' }}>Cliente registrato. Nessun accesso al pannello admin.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="user-create-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Nuovo utente</DialogTitle>
            <DialogDescription className="text-xs">
              Crea un account admin o editor. La password verra generata automaticamente se non specificata.
            </DialogDescription>
          </DialogHeader>

          {createdCred ? (
            <div className="space-y-3 pt-2" data-testid="user-created-creds">
              <div className="p-4 rounded-sm" style={{ backgroundColor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <p className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: '#059669' }}>
                  <Check className="w-3.5 h-3.5" /> Utente creato con successo
                </p>
                <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Email</p>
                <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>{createdCred.email}</p>
                <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Password (mostrata SOLO ora, salvala!)</p>
                <div className="p-2 rounded-sm flex items-center justify-between gap-2" style={{ backgroundColor: 'var(--bg-white)', border: '1px solid var(--border-light)' }}>
                  <code className="text-sm font-mono" style={{ color: 'var(--text-primary)' }}>{createdCred.password}</code>
                  <button onClick={copyCreds} className="p-1" data-testid="copy-creds-btn">
                    {copied ? <Check className="w-4 h-4" style={{ color: '#059669' }} /> : <Copy className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                  </button>
                </div>
                <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>Condividi queste credenziali in modo sicuro. Non le potrai piu vedere.</p>
              </div>
              <Button onClick={() => { setCreatedCred(null); setCreateOpen(false); }} className="w-full" style={{ backgroundColor: 'var(--accent)', color: '#fff' }} data-testid="user-close-btn">
                Chiudi
              </Button>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-xs">Email *</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  type="email" placeholder="mario.rossi@example.com" className="text-sm" data-testid="user-email-input" />
              </div>
              <div>
                <Label className="text-xs">Nome</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Mario Rossi" className="text-sm" data-testid="user-name-input" />
              </div>
              <div>
                <Label className="text-xs">Ruolo *</Label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full text-sm px-3 py-2 rounded-sm border" data-testid="user-role-input"
                  style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-white)' }}>
                  <option value="editor">Editor (gestisce contenuti, no ordini/abbonamenti)</option>
                  <option value="admin">Admin (accesso completo)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3" /> Password <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(opzionale)</span></Label>
                <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  type="text" placeholder="Lascia vuoto per generarla automaticamente" className="text-sm font-mono" data-testid="user-password-input" />
              </div>
              <Button onClick={handleCreate} disabled={busy} className="w-full mt-3" style={{ backgroundColor: 'var(--accent)', color: '#fff' }} data-testid="user-submit-btn">
                {busy ? 'Creazione...' : 'Crea Utente'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
